import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, DashboardData } from "../lib/api";

export function DashboardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get<DashboardData>("/api/v1/dashboard"),
  });

  if (isLoading) return <div style={{ padding: 24 }}>Завантаження…</div>;
  if (error) return <div style={{ padding: 24, color: "#c53030" }}>Помилка: {(error as any).detail}</div>;
  if (!data) return null;

  const distTotal = Object.values(data.scores.distribution).reduce((a, b) => a + b, 0);

  return (
    <div style={{ padding: "24px 32px" }}>
      <h1 style={{ fontSize: 22, color: "#1a365d", margin: "0 0 6px" }}>Дашборд</h1>
      <p style={{ color: "#718096", fontSize: 13, marginBottom: 20 }}>
        Огляд стану системи: лікарні, scores, виплати, інтервенції, sync.
      </p>

      {/* KPI grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 20 }}>
        <Kpi
          label="Лікарень"
          value={data.facilities.total.toLocaleString("uk-UA")}
          sub={`${data.facilities.geocoded_pct}% з координатами`}
          color="#2c5282"
        />
        <Kpi
          label="З розрахованими scores"
          value={data.scores.total_scored.toLocaleString("uk-UA")}
          sub={`${data.scores.data_sufficient} з достатніми даними`}
          color="#38a169"
        />
        <Kpi
          label="Виплат ПМГ (за весь час)"
          value={`${data.payments.total_pay_billion_uah.toLocaleString("uk-UA")} млрд ₴`}
          sub={`${data.payments.payment_records.toLocaleString("uk-UA")} платежів`}
          color="#805ad5"
        />
        <Kpi
          label="Всього послуг"
          value={data.services.total_services.toLocaleString("uk-UA")}
          sub={`${data.services.total_episodes.toLocaleString("uk-UA")} епізодів`}
          color="#d69e2e"
        />
        <Kpi
          label="Інтервенцій"
          value={data.interventions.total.toString()}
          sub={`${data.interventions.active} активних · ${(data.interventions.total_budget_uah / 1e6).toFixed(1)} млн ₴`}
          color="#dd6b20"
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* Score distribution */}
        <Card title="Розподіл лікарень за Composite">
          <div style={{ marginBottom: 8, fontSize: 12, color: "#718096" }}>
            Середнє: <strong style={{ color: "#2c5282" }}>{data.scores.avg_composite}</strong> · з {distTotal} лікарень з оцінкою
          </div>
          <DistBar
            label="🏆 Лідери (≥75)"
            count={data.scores.distribution.leaders_75plus}
            total={distTotal}
            color="#38a169"
          />
          <DistBar
            label="✅ Стабільні (50-74)"
            count={data.scores.distribution.solid_50_74}
            total={distTotal}
            color="#d69e2e"
          />
          <DistBar
            label="⚠ Слабкі (25-49)"
            count={data.scores.distribution.weak_25_49}
            total={distTotal}
            color="#dd6b20"
          />
          <DistBar
            label="🚨 Критичні (<25)"
            count={data.scores.distribution.critical_below_25}
            total={distTotal}
            color="#e53e3e"
          />
        </Card>

        {/* Avg per index */}
        <Card title="Середні бали по 5 індексах">
          <IndexBar label="Активність" value={data.scores.avg_activity} />
          <IndexBar label="Ефективність" value={data.scores.avg_efficiency} />
          <IndexBar label="Маршрутизація" value={50} placeholder />
          <IndexBar label="Фін. стійкість" value={50} placeholder />
          <IndexBar label="Динаміка" value={data.scores.avg_dynamics} />
          <div style={{ fontSize: 11, color: "#a0aec0", marginTop: 8 }}>
            Маршрутизація + Фін. стійкість — поки 50 (даних ще немає)
          </div>
        </Card>
      </div>

      {/* Top areas */}
      <Card title="Топ області за середнім Composite">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr style={{ color: "#718096", textAlign: "left" }}>
              <th style={{ padding: "6px 10px" }}>Область</th>
              <th style={{ padding: "6px 10px", textAlign: "right" }}>Лікарень</th>
              <th style={{ padding: "6px 10px", textAlign: "right" }}>Сер. Composite</th>
            </tr>
          </thead>
          <tbody>
            {data.by_area.slice(0, 15).map((a) => (
              <tr key={a.area} style={{ borderTop: "1px solid #e2e8f0" }}>
                <td style={{ padding: "6px 10px" }}>{a.area}</td>
                <td style={{ padding: "6px 10px", textAlign: "right" }}>{a.facilities}</td>
                <td style={{ padding: "6px 10px", textAlign: "right", color: scoreColor(a.avg_composite), fontWeight: 600 }}>
                  {a.avg_composite ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Sync health */}
      <Card title="Останні запуски синхронізації">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr style={{ color: "#718096", textAlign: "left" }}>
              <th style={{ padding: "6px 10px" }}>Джерело</th>
              <th style={{ padding: "6px 10px" }}>Статус</th>
              <th style={{ padding: "6px 10px" }}>Завершено</th>
              <th style={{ padding: "6px 10px", textAlign: "right" }}>Insert</th>
              <th style={{ padding: "6px 10px", textAlign: "right" }}>Update</th>
            </tr>
          </thead>
          <tbody>
            {data.last_sync.map((s) => (
              <tr key={s.source} style={{ borderTop: "1px solid #e2e8f0" }}>
                <td style={{ padding: "6px 10px", fontFamily: "ui-monospace, monospace", fontSize: 12 }}>{s.source}</td>
                <td style={{ padding: "6px 10px" }}>
                  <span style={{ color: s.status === "success" ? "#38a169" : "#e53e3e", fontWeight: 600 }}>
                    {s.status}
                  </span>
                </td>
                <td style={{ padding: "6px 10px", color: "#718096" }}>
                  {s.finished_at ? new Date(s.finished_at).toLocaleString("uk-UA") : "—"}
                </td>
                <td style={{ padding: "6px 10px", textAlign: "right" }}>{s.rows_inserted?.toLocaleString("uk-UA") ?? 0}</td>
                <td style={{ padding: "6px 10px", textAlign: "right" }}>{s.rows_updated?.toLocaleString("uk-UA") ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 10, fontSize: 12, color: "#718096" }}>
          Деталі: <Link to="/admin/uptime" style={{ color: "#4299e1" }}>/admin/uptime</Link>
          {" · "}
          <Link to="/admin/data-quality" style={{ color: "#4299e1" }}>/admin/data-quality</Link>
        </div>
      </Card>
    </div>
  );
}

function Kpi({ label, value, sub, color }: { label: string; value: string; sub?: string; color: string }) {
  return (
    <div style={{ background: "white", padding: 14, border: `2px solid ${color}30`, borderRadius: 6 }}>
      <div style={{ fontSize: 11, color: "#718096", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#a0aec0", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function DistBar({ label, count, total, color }: { label: string; count: number; total: number; color: string }) {
  const pct = total ? (count / total) * 100 : 0;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
        <span>{label}</span>
        <span style={{ color: "#4a5568" }}>{count.toLocaleString("uk-UA")} ({pct.toFixed(1)}%)</span>
      </div>
      <div style={{ height: 6, background: "#edf2f7", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color }} />
      </div>
    </div>
  );
}

function IndexBar({ label, value, placeholder }: { label: string; value: number | null; placeholder?: boolean }) {
  const v = value ?? 0;
  return (
    <div style={{ marginBottom: 7 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
        <span>{label}{placeholder && <span style={{ color: "#a0aec0", fontSize: 10, marginLeft: 4 }}>(50.0)</span>}</span>
        <span style={{ color: scoreColor(value), fontWeight: 600 }}>{value ?? "—"}</span>
      </div>
      <div style={{ height: 6, background: "#edf2f7", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${v}%`, height: "100%", background: placeholder ? "#cbd5e0" : scoreColor(value) }} />
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: "white", padding: 16, border: "1px solid #e2e8f0", borderRadius: 6, marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#2c5282", marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

function scoreColor(v: number | null | undefined): string {
  if (v === null || v === undefined) return "#a0aec0";
  if (v >= 75) return "#38a169";
  if (v >= 50) return "#d69e2e";
  if (v >= 25) return "#dd6b20";
  return "#e53e3e";
}
